import React, { Component } from "react";
import { connect } from "react-redux";
import { Redirect, withRouter } from "react-router-dom";
import Meta from "../helpers/meta";
import { getHomepageCanonical } from "../../helpers/canonicalUrl";
import { generateOrganizationSchema, generateWebSiteSchema } from "../helpers/structuredData";

import Home from "./Home";
import Phase2Home from "./Phase2/Home";
import { AppMode } from "../../constants";
import { isRelaxPath } from "../../configs/relax";
import { RUSH_ENABLED, RELAX_ENABLED } from "../../services/settings/reducer";
import { setAppMode } from "../../services/settings/actions"; // Added
import {
  getCurrentGPSLocation,
  calculateDistanceBetweenPoints,
} from "../../utils/locationUtils";
import Loading from "../helpers/loading";
import {
  detectActiveLocationSilently,
  hasActiveLocation,
  isSavedAddressActive,
  setActiveLocationFromSavedAddress,
} from "../../helpers/activeLocation";
import { isForceAddressSetupActive } from "../../helpers/addressSetupFlow";

class Mobile extends Component {
  state = {
    showGdpr: false,
    checkingLocation: false,
    locationManuallySelected: false, // Track if location was manually selected to prevent GPS updates
    resolvingLocation: false,
  };

  componentDidMount() {
    this.ensureActiveLocation();

    // START: Sync App Mode with URL
    const { pathname } = this.props.location;
    if (pathname && isRelaxPath(pathname) && this.props.appMode !== AppMode.RELAY) {
      this.props.setAppMode(AppMode.RELAY);
    } else if (pathname === "/" && this.props.appMode !== (RUSH_ENABLED ? AppMode.SPRINT : AppMode.RELAY)) {
      this.props.setAppMode(RUSH_ENABLED ? AppMode.SPRINT : AppMode.RELAY);
    }
    // END: Sync App Mode with URL

    setTimeout(() => {
      if (document.getElementsByClassName("popup-content")[0]) {
        document.getElementsByClassName(
          "popup-content"
        )[0].style.backgroundColor = "transparent";
      }
    }, 100);

    if (!localStorage.getItem("gdprAccepted")) {
      localStorage.setItem("gdprAccepted", "false");
      if (localStorage.getItem("showGdpr") === "true") {
        this.setState({ showGdpr: true });
      }
    }

    if (
      localStorage.getItem("showGdpr") === "true" &&
      localStorage.getItem("gdprAccepted") === "false"
    ) {
      this.setState({ showGdpr: true });
    }

    // Check if location was manually selected in current session
    // If yes, skip location check entirely (don't even call it)
    // This prevents GPS check from running on back navigation when user has manually selected location
    const locationManuallyChanged = sessionStorage.getItem("locationManuallyChanged");

    // CRITICAL: If flag is set, ALWAYS skip location check - no exceptions
    // This ensures manual selection is respected during all navigation (back, forward, etc.)
    if (locationManuallyChanged === "true") {
      // IMPORTANT: Set a flag in component state to prevent any location updates
      // This provides an additional layer of protection
      this.setState({ locationManuallySelected: true });

      return; // Don't run location check at all - this is the critical return
    } else {
      this.setState({ locationManuallySelected: false });
    }

    // Increased delay before checking location to ensure any navigation/state changes are complete
    // This prevents race condition where location check runs before manual selection flag is set
    // 500ms gives enough time for localStorage writes to persist after navigation
    setTimeout(() => {
      // Check location on app load if user has saved address
      // Only runs if location was NOT manually selected in current session
      this.checkAndUpdateLocation();
    }, 500);
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.user !== this.props.user) {
      this.ensureActiveLocation();
    }
    // START: Sync App Mode with URL on navigation
    if (this.props.location.pathname !== prevProps.location.pathname) {
      const { pathname } = this.props.location;
      if (pathname && isRelaxPath(pathname) && this.props.appMode !== AppMode.RELAY) {
        this.props.setAppMode(AppMode.RELAY);
      } else if (pathname === "/" && this.props.appMode !== (RUSH_ENABLED ? AppMode.SPRINT : AppMode.RELAY)) {
        this.props.setAppMode(RUSH_ENABLED ? AppMode.SPRINT : AppMode.RELAY);
      }
    }
    // END: Sync App Mode with URL on navigation
  }

  /**
   * A signed-in user with no location should not be sent back to onboarding.
   * Reuse their default address, else resolve GPS silently when the permission
   * is already granted, and only fall back to the picker when we must ask.
   */
  ensureActiveLocation = async () => {
    if (isForceAddressSetupActive()) return;

    const { user } = this.props;
    if (!user || !user.success) return;

    // Their own address beats a GPS reading, unless they picked a different
    // location during this session — that choice stays until they leave.
    const pickedThisSession =
      sessionStorage.getItem("locationManuallyChanged") === "true";
    const defaultAddress = user.data && user.data.default_address;
    if (!pickedThisSession && !isSavedAddressActive() && defaultAddress) {
      if (setActiveLocationFromSavedAddress(defaultAddress)) {
        this.forceUpdate();
        return;
      }
    }

    if (hasActiveLocation()) return;

    this.setState({ resolvingLocation: true });
    await detectActiveLocationSilently();
    if (this._unmounted) return;
    this.setState({ resolvingLocation: false });
  };

  checkAndUpdateLocation = async () => {
    // Blinkit-like location logic:
    // 1. Always check GPS on app load/refresh (every componentDidMount = potential refresh/reopen)
    // 2. Compare with saved address
    // 3. If distance > 200m AND not manually selected in current session → update to GPS
    // 4. If manually selected in current session (sessionStorage flag = "true") → respect user's choice, don't auto-update
    // Note: sessionStorage is automatically cleared on refresh/reopen, so every mount = fresh GPS check

    // CRITICAL: Multiple defensive checks before doing anything
    // Check 1: Component state (set in componentDidMount)
    if (this.state.locationManuallySelected === true) {
      this.setState({ checkingLocation: false });
      return;
    }

    // Check 2: sessionStorage flag
    const locationManuallyChanged = sessionStorage.getItem("locationManuallyChanged");

    // If user manually selected location in current session, respect their choice (don't auto-update)
    if (locationManuallyChanged === "true") {
      // Update component state as well for consistency
      this.setState({ locationManuallySelected: true, checkingLocation: false });
      return;
    }

    const savedAddress = localStorage.getItem("userSetAddress");

    // If no saved address, try to get GPS location
    // BUT: Don't auto-set location if user is not logged in (let them complete OTP login first)
    if (!savedAddress) {
      // Check if user is logged in before auto-setting location
      const user = this.props.user;
      if (!user || !user.success) {
        this.setState({ checkingLocation: false });
        return;
      }
      try {
        this.setState({ checkingLocation: true });
        const currentLocation = await getCurrentGPSLocation({
          timeout: 10000,
          enableHighAccuracy: true,
          maximumAge: 0,
        });

        const { reverseGeocode } = await import("../../utils/locationUtils");
        const currentAddressData = await reverseGeocode(currentLocation.lat, currentLocation.lng);

        const newAddress = {
          lat: currentLocation.lat.toString(),
          lng: currentLocation.lng.toString(),
          address: currentAddressData.formatted_address,
          house: null,
          tag: null,
        };

        localStorage.setItem("userSetAddress", JSON.stringify(newAddress));
        localStorage.setItem("geoLocation", JSON.stringify({
          formatted_address: currentAddressData.formatted_address,
          geometry: {
            location: {
              lat: currentLocation.lat,
              lng: currentLocation.lng,
            },
          },
        }));
        localStorage.setItem("userLat", currentLocation.lat.toString());
        localStorage.setItem("userLng", currentLocation.lng.toString());

        this.setState({ checkingLocation: false });
        return;
      } catch (error) {
        this.setState({ checkingLocation: false });
        return;
      }
    }

    // Parse saved address
    let parsedAddress;
    try {
      parsedAddress = JSON.parse(savedAddress);
      if (!parsedAddress.lat || !parsedAddress.lng) {
        this.setState({ checkingLocation: false });
        return;
      }
    } catch (e) {
      console.error("Error parsing saved address:", e);
      this.setState({ checkingLocation: false });
      return;
    }

    // Always get fresh GPS location (Blinkit behavior)
    try {
      this.setState({ checkingLocation: true });
      const currentLocation = await getCurrentGPSLocation({
        timeout: 10000,
        enableHighAccuracy: true,
        maximumAge: 0, // Always get fresh location
      });

      // Calculate distance between saved address and current GPS
      const distance = calculateDistanceBetweenPoints(
        parseFloat(parsedAddress.lat),
        parseFloat(parsedAddress.lng),
        currentLocation.lat,
        currentLocation.lng
      );

      const distanceMeters = distance * 1000;

      // Blinkit standard: If distance > 200 meters, update to current GPS location
      const DISTANCE_THRESHOLD_METERS = 200;

      if (distanceMeters > DISTANCE_THRESHOLD_METERS) {
        // CRITICAL: Double-check sessionStorage flag right before updating (defensive check)
        // This prevents race condition where flag is set after initial check but before update
        const finalFlagCheck = sessionStorage.getItem("locationManuallyChanged");
        if (finalFlagCheck === "true") {
          this.setState({ checkingLocation: false });
          return;
        }

        // Get address for current GPS location
        try {
          const { reverseGeocode } = await import("../../utils/locationUtils");
          const currentAddressData = await reverseGeocode(currentLocation.lat, currentLocation.lng);
          const currentAddressText = currentAddressData.formatted_address;

          // One more sessionStorage flag check after reverse geocoding (in case it took time)
          const postGeocodeFlagCheck = sessionStorage.getItem("locationManuallyChanged");
          if (postGeocodeFlagCheck === "true") {
            this.setState({ checkingLocation: false });
            return;
          }

          // CRITICAL: Final check right before saving to localStorage
          // This is the last chance to prevent overwriting manual selection
          const finalSaveFlagCheck = sessionStorage.getItem("locationManuallyChanged");
          if (finalSaveFlagCheck === "true") {
            this.setState({ checkingLocation: false });
            return;
          }

          // Update location to current GPS
          const updatedAddress = {
            lat: currentLocation.lat.toString(),
            lng: currentLocation.lng.toString(),
            address: currentAddressText,
            house: null, // Clear house/tag when auto-updating (GPS location)
            tag: null,
          };

          // Save to localStorage
          localStorage.setItem("geoLocation", JSON.stringify({
            formatted_address: currentAddressText,
            geometry: {
              location: {
                lat: currentLocation.lat,
                lng: currentLocation.lng,
              },
            },
          }));
          localStorage.setItem("userLat", currentLocation.lat.toString());
          localStorage.setItem("userLng", currentLocation.lng.toString());
          localStorage.setItem("userSetAddress", JSON.stringify(updatedAddress));

          // IMPORTANT: Don't set locationManuallyChanged flag for auto-updates
          // This allows future auto-updates if user moves significantly

          this.setState({ checkingLocation: false });
          // Location updated in localStorage; home will use new location on next navigation or re-render
        } catch (error) {
          console.warn("Failed to reverse geocode, keeping saved location:", error);
          this.setState({ checkingLocation: false });
        }
      } else {
        this.setState({ checkingLocation: false });
      }
    } catch (error) {
      // GPS failed or permission denied - keep using saved location
      this.setState({ checkingLocation: false });
    }
  }
  handleGdprClick = () => {
    localStorage.setItem("gdprAccepted", "true");
    this.setState({ showGdpr: false });
  };

  renderHomeOrLocationGate = (isPhase2) => {
    if (hasActiveLocation()) {
      return <div>{isPhase2 ? <Phase2Home /> : <Home />}</div>;
    }
    if (this.state.resolvingLocation) {
      return <Loading />;
    }
    const { user } = this.props;
    if (user && user.success) {
      return <Redirect to="/my-location" />;
    }
    return <Redirect to="/splashscreen" />;
  };

  render() {
    // Relax is "coming soon": send any /relax link back to the Rush home.
    if (!RELAX_ENABLED && isRelaxPath(this.props.location.pathname)) {
      return <Redirect to="/" />;
    }
    const { ogtitle, ogdescription, ogimage, ogurl } = this.props;

    // Generate structured data for homepage
    const organizationSchema = generateOrganizationSchema({
      name: localStorage.getItem("storeName") || '',
      url: window.location.origin,
      description: localStorage.getItem("seoMetaDescription") || ''
    });

    const websiteSchema = generateWebSiteSchema({
      name: localStorage.getItem("storeName") || '',
      url: window.location.origin,
      description: localStorage.getItem("seoMetaDescription") || '',
      potentialAction: {
        target: window.location.origin + '/search-location?q={search_term_string}'
      }
    });

    const isPhase2 = isRelaxPath(this.props.location.pathname);

    return (
      <React.Fragment>
        <Meta
          seotitle={localStorage.getItem("seoMetaTitle") || ''}
          seodescription={localStorage.getItem("seoMetaDescription") || ''}
          ogtype="website"
          ogtitle={ogtitle || localStorage.getItem("seoMetaTitle") || ''}
          ogdescription={ogdescription || localStorage.getItem("seoMetaDescription") || ''}
          ogurl={ogurl || getHomepageCanonical()}
          ogimg={ogimage || ''}
          canonicalUrl={getHomepageCanonical()}
          twittertitle={localStorage.getItem("seoTwitterTitle") || ''}
          twitterdescription={localStorage.getItem("seoTwitterDescription") || ''}
          structuredData={[organizationSchema, websiteSchema]}
        />
        {/* {this.state.showGdpr && (
          <div className="fixed-gdpr-mobile">
            <span
              dangerouslySetInnerHTML={{
                __html: localStorage.getItem("gdprMessage"),
              }}
            />
            <span>
              <button
                className="btn btn-sm ml-2"
                style={{ backgroundColor: localStorage.getItem("storeColor") }}
                onClick={this.handleGdprClick}
              >
                {localStorage.getItem("gdprConfirmButton")}
              </button>
            </span>
          </div>
        )} */}
        {this.renderHomeOrLocationGate(isPhase2)}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  ogtitle: state.items.ogtitle,
  ogdescription: state.items.ogdescription,
  ogimage: state.items.ogimage,
  ogurl: state.items.ogurl,
  appMode: state.settings.appMode,
  user: state.user.user,
});

export default connect(mapStateToProps, { setAppMode })(withRouter(Mobile));
